/**
 * POS Cash Tender Modal
 * Allows cashier to enter amount received and calculates change
 * Optimized for tablet/touchscreen use with proper touch event handling
 */
import { useState, useEffect, useCallback } from 'react';
import { X, Banknote, Calculator, Check } from 'lucide-react';

export default function POSCashTenderModal({
  isOpen,
  onClose,
  orderTotal,
  onConfirm,
  orderProcessing
}) {
  const [amountTendered, setAmountTendered] = useState('');
  
  // Quick cash buttons - common denominations in Fiji
  const quickAmounts = [5, 10, 20, 50, 100];
  
  // Calculate change
  const tenderedValue = parseFloat(amountTendered) || 0;
  const changeDue = tenderedValue - orderTotal;
  // Allow $0 tendered if order total is $0 (e.g., free Bula Pass redemption)
  const isValidAmount = orderTotal === 0 ? true : (tenderedValue >= orderTotal && tenderedValue > 0);
  
  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setAmountTendered('');
    }
  }, [isOpen]);
  
  // Handle numpad button click
  const handleNumpadClick = useCallback((value) => {
    if (value === 'backspace') {
      setAmountTendered(prev => prev.slice(0, -1));
    } else if (value === '.') {
      setAmountTendered(prev => {
        if (!prev.includes('.')) {
          return prev + '.';
        }
        return prev;
      });
    } else {
      setAmountTendered(prev => {
        const parts = prev.split('.');
        if (parts.length === 2 && parts[1].length >= 2) return prev;
        return prev + value;
      });
    }
  }, []);
  
  // Handle quick amount click
  const handleQuickAmount = useCallback((amount) => {
    setAmountTendered(amount.toString());
  }, []);
  
  // Handle exact amount
  const handleExactAmount = useCallback(() => {
    setAmountTendered(orderTotal.toFixed(2));
  }, [orderTotal]);
  
  // Handle confirm - wrapped in useCallback to avoid re-renders
  const handleConfirm = useCallback(() => {
    const currentTendered = parseFloat(amountTendered) || 0;
    const currentChange = currentTendered - orderTotal;
    // Allow $0 tendered if order total is $0 (e.g., free Bula Pass redemption)
    const valid = orderTotal === 0 ? true : (currentTendered >= orderTotal && currentTendered > 0);
    
    if (valid && !orderProcessing) {
      onConfirm({
        amountTendered: currentTendered,
        changeDue: currentChange
      });
    }
  }, [amountTendered, orderTotal, orderProcessing, onConfirm]);
  
  if (!isOpen) return null;
  
  const safeOrderTotal = typeof orderTotal === 'number' ? orderTotal : 0;
  
  return (
    <div 
      className="cash-tender-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="cash-tender-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="cash-tender-header">
          <div className="cash-tender-header-title">
            <Banknote size={24} />
            <span>Cash Payment</span>
          </div>
          <button 
            type="button"
            onClick={onClose}
            className="cash-tender-close-btn"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Order Total */}
        <div className="cash-tender-total-section">
          <p className="cash-tender-label">Order Total</p>
          {safeOrderTotal === 0 ? (
            <p className="cash-tender-total-amount" style={{color: '#10b981'}}>FREE (Bula Pass)</p>
          ) : (
            <p className="cash-tender-total-amount">${safeOrderTotal.toFixed(2)}</p>
          )}
        </div>
        
        {/* Scrollable Content */}
        <div className="cash-tender-content">
          {/* For $0 orders, show a simple confirm button */}
          {safeOrderTotal === 0 ? (
            <div style={{textAlign: 'center', padding: '20px'}}>
              <div style={{
                background: 'linear-gradient(135deg, #10b981, #059669)',
                borderRadius: '16px',
                padding: '24px',
                marginBottom: '20px',
                color: 'white'
              }}>
                <p style={{fontSize: '18px', fontWeight: 'bold', marginBottom: '8px'}}>
                  ☕ Free Bula Pass Coffee
                </p>
                <p style={{fontSize: '14px', opacity: 0.9}}>
                  No payment required - just confirm!
                </p>
              </div>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={orderProcessing}
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Check size={24} />
                Confirm Free Order
              </button>
            </div>
          ) : (
            <>
              {/* Amount Input Display */}
              <div className="cash-tender-input-section">
                <p className="cash-tender-label">Amount Received</p>
                <div className={`cash-tender-input-display ${isValidAmount ? 'valid' : ''}`}>
                  <span className="cash-tender-input-value">
                    ${amountTendered || '0.00'}
                  </span>
                </div>
              </div>
          
          {/* Quick Amount Buttons */}
          <div className="cash-tender-quick-amounts">
            {quickAmounts.map(amount => (
              <button
                key={amount}
                type="button"
                onClick={() => handleQuickAmount(amount)}
                className="cash-tender-quick-btn"
              >
                ${amount}
              </button>
            ))}
          </div>
          
          {/* Exact Amount Button */}
          <button
            type="button"
            onClick={handleExactAmount}
            className="cash-tender-exact-btn"
            data-testid="cash-tender-exact-amount"
          >
            <Calculator size={18} />
            Exact (${safeOrderTotal.toFixed(2)})
          </button>
          
          {/* Numpad */}
          <div className="cash-tender-numpad">
            {['7', '8', '9', '4', '5', '6', '1', '2', '3', '.', '0', '⌫'].map(key => (
              <button
                key={key}
                type="button"
                onClick={() => handleNumpadClick(key === '⌫' ? 'backspace' : key)}
                className={`cash-tender-numpad-btn ${key === '⌫' ? 'backspace' : ''}`}
              >
                {key}
              </button>
            ))}
          </div>
          
          {/* Change Due Display */}
          {amountTendered && tenderedValue > 0 && (
            <div className={`cash-tender-change-display ${isValidAmount ? 'valid' : 'invalid'}`}>
              <p className="cash-tender-change-label">
                {isValidAmount ? 'Change Due' : 'Amount Short'}
              </p>
              <p className="cash-tender-change-amount">
                ${Math.abs(changeDue).toFixed(2)}
              </p>
            </div>
          )}
            </>
          )}
        </div>
        
        {/* Footer Buttons - Only show for non-free orders */}
        {safeOrderTotal > 0 && (
        <div className="cash-tender-footer">
          <button
            type="button"
            onClick={onClose}
            className="cash-tender-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!isValidAmount || orderProcessing}
            className={`cash-tender-confirm-btn ${isValidAmount && !orderProcessing ? 'active' : 'disabled'}`}
            data-testid="confirm-cash-payment"
          >
            {orderProcessing ? (
              <>
                <span className="cash-tender-spinner"></span>
                Processing...
              </>
            ) : (
              <>
                <Check size={20} />
                Complete Sale
              </>
            )}
          </button>
        </div>
        )}
      </div>
      
      <style>{`
        .cash-tender-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0,0,0,0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 16px;
          overflow-y: auto;
        }
        
        .cash-tender-modal {
          background-color: white;
          border-radius: 12px;
          width: 100%;
          max-width: 340px;
          max-height: 90vh;
          margin: auto;
          box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        
        .cash-tender-header {
          background-color: #16a34a;
          color: white;
          padding: 10px 14px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-radius: 12px 12px 0 0;
          flex-shrink: 0;
        }
        
        .cash-tender-header-title {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 16px;
          font-weight: bold;
        }
        
        .cash-tender-close-btn {
          background: rgba(255,255,255,0.2);
          border: none;
          border-radius: 50%;
          padding: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          -webkit-tap-highlight-color: transparent;
        }
        
        .cash-tender-close-btn:active {
          background: rgba(255,255,255,0.4);
        }
        
        .cash-tender-total-section {
          background-color: #f3f4f6;
          padding: 8px;
          text-align: center;
          border-bottom: 1px solid #e5e7eb;
          flex-shrink: 0;
        }
        
        .cash-tender-label {
          font-size: 10px;
          color: #6b7280;
          text-transform: uppercase;
          margin-bottom: 2px;
          margin-top: 0;
        }
        
        .cash-tender-total-amount {
          font-size: 24px;
          font-weight: bold;
          color: #111827;
          margin: 0;
        }
        
        .cash-tender-content {
          padding: 10px;
          flex: 1;
          overflow-y: auto;
          min-height: 0;
        }
        
        .cash-tender-input-section {
          margin-bottom: 8px;
        }
        
        .cash-tender-input-display {
          border: 2px solid #d1d5db;
          border-radius: 8px;
          padding: 8px;
          text-align: center;
          background-color: #f9fafb;
          transition: all 0.2s;
        }
        
        .cash-tender-input-display.valid {
          border-color: #16a34a;
          background-color: #f0fdf4;
        }
        
        .cash-tender-input-value {
          font-size: 26px;
          font-weight: bold;
          color: #111827;
        }
        
        .cash-tender-quick-amounts {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 4px;
          margin-bottom: 6px;
        }
        
        .cash-tender-quick-btn {
          height: 36px;
          font-size: 13px;
          font-weight: 600;
          border: 2px solid #d1d5db;
          border-radius: 6px;
          background-color: white;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
        }
        
        .cash-tender-quick-btn:active {
          background-color: #f0fdf4;
          border-color: #16a34a;
          transform: scale(0.95);
        }
        
        .cash-tender-exact-btn {
          width: 100%;
          height: 36px;
          font-size: 13px;
          font-weight: 600;
          border: 2px solid #16a34a;
          border-radius: 6px;
          background-color: #f0fdf4;
          color: #16a34a;
          cursor: pointer;
          margin-bottom: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
        }
        
        .cash-tender-exact-btn:active {
          background-color: #dcfce7;
          transform: scale(0.98);
        }
        
        .cash-tender-numpad {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 4px;
          margin-bottom: 8px;
        }
        
        .cash-tender-numpad-btn {
          height: 44px;
          font-size: 20px;
          font-weight: bold;
          border: 2px solid #d1d5db;
          border-radius: 6px;
          background-color: white;
          color: #111827;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
          user-select: none;
        }
        
        .cash-tender-numpad-btn.backspace {
          color: #ef4444;
        }
        
        .cash-tender-numpad-btn:active {
          background-color: #f3f4f6;
          transform: scale(0.95);
        }
        
        .cash-tender-change-display {
          padding: 8px;
          border-radius: 8px;
          text-align: center;
        }
        
        .cash-tender-change-display.valid {
          background-color: #dcfce7;
          border: 2px solid #16a34a;
        }
        
        .cash-tender-change-display.invalid {
          background-color: #fee2e2;
          border: 2px solid #ef4444;
        }
        
        .cash-tender-change-label {
          font-size: 10px;
          text-transform: uppercase;
          margin-bottom: 2px;
          margin-top: 0;
        }
        
        .cash-tender-change-display.valid .cash-tender-change-label {
          color: #166534;
        }
        
        .cash-tender-change-display.invalid .cash-tender-change-label {
          color: #991b1b;
        }
        
        .cash-tender-change-amount {
          font-size: 24px;
          font-weight: bold;
          margin: 0;
        }
        
        .cash-tender-change-display.valid .cash-tender-change-amount {
          color: #16a34a;
        }
        
        .cash-tender-change-display.invalid .cash-tender-change-amount {
          color: #ef4444;
        }
        
        .cash-tender-footer {
          padding: 10px;
          border-top: 1px solid #e5e7eb;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
          background-color: white;
          border-radius: 0 0 12px 12px;
          flex-shrink: 0;
        }
        
        .cash-tender-cancel-btn {
          height: 44px;
          font-size: 14px;
          font-weight: 600;
          border: 2px solid #d1d5db;
          border-radius: 8px;
          background-color: white;
          color: #374151;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
        }
        
        .cash-tender-cancel-btn:active {
          background-color: #f3f4f6;
        }
        
        .cash-tender-confirm-btn {
          height: 44px;
          font-size: 14px;
          font-weight: 600;
          border: none;
          border-radius: 8px;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
        }
        
        .cash-tender-confirm-btn.active {
          background-color: #16a34a;
          cursor: pointer;
        }
        
        .cash-tender-confirm-btn.active:active {
          background-color: #15803d;
          transform: scale(0.98);
        }
        
        .cash-tender-confirm-btn.disabled {
          background-color: #9ca3af;
          cursor: not-allowed;
          opacity: 0.7;
        }
        
        .cash-tender-spinner {
          width: 20px;
          height: 20px;
          border: 3px solid white;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        /* Ensure buttons don't get focus styling issues on touch */
        .cash-tender-modal button:focus {
          outline: none;
        }
        
        /* Tablet/larger screen optimizations */
        @media (min-width: 768px) {
          .cash-tender-overlay {
            align-items: center;
          }
        }
      `}</style>
    </div>
  );
}
